import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronDown,
  ClipboardCheck,
  DollarSign,
  Download,
  ExternalLink,
  FileText,
  Lock,
  TrendingUp,
  Workflow,
} from 'lucide-react';
import ManageAccessModal from './ManageAccessModal';

const ROLE_LABEL = {
  cbv: 'CBV',
  business_broker: 'BROKER',
  lawyer: 'LAWYER',
  accountant: 'ACCOUNTANT',
  lender: 'LENDER',
  cepa_coach: 'CEPA',
};

// The 4 snapshot source types share-snapshot-with-deal-team produces. Any
// other (or null) source_type renders as a flat file row.
const SNAPSHOT_SOURCE_TYPES = new Set([
  'valuation_snapshot',
  'financing_snapshot',
  'deal_structure_snapshot',
  'assessment_snapshot',
]);

// Per-snapshot-type display config — icon, title, and live-link target.
// Icons match ShareSnapshotModal so the picker icon and the row icon are
// the same for each type.
const SNAPSHOT_TYPE_CONFIG = {
  valuation_snapshot: {
    title: 'Valuation Snapshot',
    icon: TrendingUp,
    sourcePath: '/succeedo/valuation',
  },
  financing_snapshot: {
    title: 'Financing Scenario',
    icon: DollarSign,
    sourcePath: '/succeedo/financing-calculator',
  },
  deal_structure_snapshot: {
    title: 'Deal Structure',
    icon: Workflow,
    sourcePath: '/succeedo/deal-structure',
  },
  assessment_snapshot: {
    title: 'Readiness Assessment',
    icon: ClipboardCheck,
    sourcePath: '/succeedo/assessment',
  },
};

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (e) {
    return '';
  }
}

// Group rows by `source_type + source_id` for snapshot lineage. Files (rows
// where source_type isn't one of the 4 snapshot types) stay flat. Returns
// an array of items sorted most-recent first, where each item is either:
//   { kind: 'file', row, sortKey }
//   { kind: 'snapshot', key, versions, latest, sortKey }
function groupRows(documents) {
  const fileRows = [];
  const snapshotGroups = new Map();

  documents.forEach((doc) => {
    if (SNAPSHOT_SOURCE_TYPES.has(doc.source_type) && doc.source_id != null) {
      const key = `${doc.source_type}:${doc.source_id}`;
      if (!snapshotGroups.has(key)) snapshotGroups.set(key, { key, versions: [] });
      snapshotGroups.get(key).versions.push(doc);
    } else {
      fileRows.push(doc);
    }
  });

  // Sort each snapshot group oldest → newest so v1 is first, latest is last.
  snapshotGroups.forEach((group) => {
    group.versions.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  });

  const items = [];
  fileRows.forEach((f) => items.push({ kind: 'file', row: f, sortKey: f.created_at }));
  snapshotGroups.forEach(({ key, versions }) => {
    const latest = versions[versions.length - 1];
    items.push({ kind: 'snapshot', key, versions, latest, sortKey: latest.created_at });
  });

  // Sort by most recent first (latest version's created_at for snapshots).
  items.sort((a, b) => new Date(b.sortKey) - new Date(a.sortKey));
  return items;
}

const RoleBadges = ({ roles }) => {
  if (!roles || roles.length === 0) {
    return (
      <span className="font-mono text-[10px] uppercase text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/50 px-1.5 py-0.5 rounded">
        ALL
      </span>
    );
  }
  return roles.map((role) => (
    <span
      key={role}
      className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-1.5 py-0.5 rounded"
    >
      {ROLE_LABEL[role] || role}
    </span>
  ));
};

const FileRow = ({ doc, currentUserId, teamOwnerUserId, onManageAccess }) => {
  // Only the team owner OR the doc uploader can manage access — same actors
  // who can already access regardless of role restrictions.
  const canManage =
    !!onManageAccess &&
    !!currentUserId &&
    (String(doc.user_id) === String(currentUserId) ||
      String(teamOwnerUserId) === String(currentUserId));

  return (
    <li className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))]/30 transition-colors">
      <FileText className="w-5 h-5 text-[hsl(var(--muted-foreground))] flex-shrink-0" />

      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-[hsl(var(--foreground))] truncate">
          {doc.title || doc.file_name || 'Untitled'}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {doc.uploaded_by_name && (
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
              {doc.uploaded_by_name}
            </span>
          )}
          {doc.created_at && (
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]/70">
              · {formatDate(doc.created_at)}
            </span>
          )}
          {doc.file_size != null && (
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]/70">
              · {formatBytes(doc.file_size)}
            </span>
          )}
          <RoleBadges roles={doc.shared_with_roles} />
        </div>
      </div>

      {canManage && (
        <button
          type="button"
          onClick={() => onManageAccess(doc)}
          aria-label="Manage access"
          className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex-shrink-0 transition-colors"
        >
          <Lock className="w-4 h-4" />
        </button>
      )}

      {doc.download_url && (
        <a
          href={doc.download_url}
          className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex-shrink-0"
          download
        >
          <Download className="w-4 h-4" />
        </a>
      )}
    </li>
  );
};

const SnapshotRow = ({
  dealTeamId,
  groupKey,
  versions,
  latest,
  expanded,
  onToggle,
  currentUserId,
  teamOwnerUserId,
  onManageAccess,
}) => {
  const cfg = SNAPSHOT_TYPE_CONFIG[latest.source_type] || {};
  const Icon = cfg.icon || TrendingUp;
  // Read-only per-snapshot deal team view (see todos/100). Team owner and
  // members both resolve via the same route; the controller scopes to
  // membership + role.
  const liveHref = dealTeamId ? `/succeedo/teams/${dealTeamId}/snapshots/${latest.id}` : null;

  // Manage access on the latest version of the snapshot — earlier versions
  // are immutable history, only the head needs role updates. Same gate as
  // FileRow: doc uploader OR team owner.
  const canManage =
    !!onManageAccess &&
    !!currentUserId &&
    (String(latest.user_id) === String(currentUserId) ||
      String(teamOwnerUserId) === String(currentUserId));

  return (
    <li className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))]/30 transition-colors">
      <div className="flex items-center gap-3 p-3">
        <div className="w-9 h-9 rounded-full bg-[hsl(var(--succeedo-orange))]/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-[hsl(var(--succeedo-orange))]" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-medium text-[hsl(var(--foreground))] truncate">
              {cfg.title || 'Snapshot'}
            </span>
            <span className="font-mono text-[10px] uppercase text-[hsl(var(--muted-foreground))]">
              · v{versions.length}
            </span>
            <span className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-1.5 py-0.5 rounded">
              LIVE
            </span>
          </div>

          {latest.note && (
            <div className="text-[12px] italic text-[hsl(var(--muted-foreground))] truncate mt-0.5">
              &ldquo;{latest.note}&rdquo;
            </div>
          )}

          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {latest.uploaded_by_name && (
              <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                {latest.uploaded_by_name}
              </span>
            )}
            {latest.created_at && (
              <span className="text-[11px] text-[hsl(var(--muted-foreground))]/70">
                · {formatDate(latest.created_at)}
              </span>
            )}
            <RoleBadges roles={latest.shared_with_roles} />
          </div>
        </div>

        {canManage && (
          <button
            type="button"
            onClick={() => onManageAccess(latest)}
            aria-label="Manage access"
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex-shrink-0 transition-colors"
          >
            <Lock className="w-4 h-4" />
          </button>
        )}

        {liveHref && (
          <a
            href={liveHref}
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex-shrink-0"
            aria-label="View live snapshot"
            onClick={() => {
              if (window.ahoy) {
                window.ahoy.track('succeedo_deal_team_document_view_clicked', {
                  source_type: latest.source_type,
                  source_id: latest.source_id,
                });
              }
            }}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}

        {versions.length > 1 && (
          <button
            type="button"
            onClick={() => onToggle(groupKey)}
            aria-label={expanded ? 'Hide version history' : 'Show version history'}
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex-shrink-0 transition-colors"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {expanded && versions.length > 1 && (
        <div className="border-t border-[hsl(var(--border))] px-3 py-3 bg-[hsl(var(--muted))]/20">
          <div className="ml-12 border-l border-[hsl(var(--border))] pl-3 space-y-2">
            <div className="font-mono text-[10px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
              Version history
            </div>
            {[...versions].reverse().map((version, idx) => {
              const versionNumber = versions.length - idx;
              // Each version is a separate VaultDocument row; deep-link
              // to its read-only deal team snapshot view.
              const versionHref = dealTeamId
                ? `/succeedo/teams/${dealTeamId}/snapshots/${version.id}`
                : null;
              return (
                <div key={version.id} className="text-[12px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] uppercase bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-1.5 py-0.5 rounded">
                      v{versionNumber}
                    </span>
                    <span className="text-[hsl(var(--muted-foreground))]">
                      · {formatDate(version.created_at)}
                    </span>
                    <RoleBadges roles={version.shared_with_roles} />
                  </div>
                  {version.note && (
                    <div className="text-[12px] italic text-[hsl(var(--muted-foreground))] mt-0.5">
                      &ldquo;{version.note}&rdquo;
                    </div>
                  )}
                  {versionHref && (
                    <a
                      href={versionHref}
                      className="text-[11px] text-[hsl(var(--succeedo-orange))] hover:underline mt-0.5 inline-block"
                    >
                      View this version
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </li>
  );
};

const DocumentsTab = ({
  dealTeamId,
  documents = [],
  canUpload = true,
  onUpload,
  onShareSnapshot,
  currentUserId,
  teamOwnerUserId,
  onAccessChanged,
}) => {
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  // null when closed; otherwise the doc whose modal is open. Single piece
  // of state covers both file and snapshot rows since the modal only ever
  // operates on one doc at a time.
  const [manageAccessDoc, setManageAccessDoc] = useState(null);

  const toggle = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleUploadClick = () => {
    if (onUpload) onUpload();
    if (window.ahoy) {
      window.ahoy.track('succeedo_deal_team_document_uploaded');
    }
  };

  const handleShareSnapshotClick = () => {
    if (onShareSnapshot) onShareSnapshot();
  };

  const handleManageAccess = (doc) => setManageAccessDoc(doc);
  const handleManageAccessClose = () => setManageAccessDoc(null);
  const handleManageAccessSuccess = () => {
    if (onAccessChanged) onAccessChanged();
  };

  const items = groupRows(documents);

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-[24px] font-light text-[hsl(var(--foreground))]">
          Shared Documents
        </h2>
        <div className="flex items-center gap-2">
          {onShareSnapshot && (
            <button
              type="button"
              onClick={handleShareSnapshotClick}
              className="px-3 py-1.5 text-[12px] font-mono uppercase tracking-wide rounded border border-[hsl(var(--succeedo-orange))]/40 text-[hsl(var(--succeedo-orange))] hover:bg-[hsl(var(--succeedo-orange))]/10 transition-colors"
            >
              + Share Snapshot
            </button>
          )}
          {canUpload && (
            <button
              type="button"
              onClick={handleUploadClick}
              className="px-3 py-1.5 text-[12px] font-mono uppercase tracking-wide rounded border border-[hsl(var(--succeedo-orange))]/40 text-[hsl(var(--succeedo-orange))] hover:bg-[hsl(var(--succeedo-orange))]/10 transition-colors"
            >
              + Upload
            </button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-[hsl(var(--muted-foreground))] text-[14px]">
          Nothing shared yet. Upload a file or share a snapshot from your work in Succeedo.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => {
            if (item.kind === 'file') {
              return (
                <FileRow
                  key={item.row.id}
                  doc={item.row}
                  currentUserId={currentUserId}
                  teamOwnerUserId={teamOwnerUserId}
                  onManageAccess={handleManageAccess}
                />
              );
            }
            return (
              <SnapshotRow
                key={item.key}
                dealTeamId={dealTeamId}
                groupKey={item.key}
                versions={item.versions}
                latest={item.latest}
                expanded={expandedKeys.has(item.key)}
                onToggle={toggle}
                currentUserId={currentUserId}
                teamOwnerUserId={teamOwnerUserId}
                onManageAccess={handleManageAccess}
              />
            );
          })}
        </ul>
      )}

      {manageAccessDoc && dealTeamId && (
        <ManageAccessModal
          open
          onClose={handleManageAccessClose}
          onSuccess={handleManageAccessSuccess}
          dealTeamId={String(dealTeamId)}
          vaultDocumentId={String(manageAccessDoc.id)}
          documentTitle={manageAccessDoc.title || manageAccessDoc.file_name || 'Document'}
          currentSharedWithRoles={manageAccessDoc.shared_with_roles || []}
        />
      )}
    </div>
  );
};

const documentShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  file_name: PropTypes.string,
  file_size: PropTypes.number,
  download_url: PropTypes.string,
  shared_with_roles: PropTypes.arrayOf(PropTypes.string),
  uploaded_by_name: PropTypes.string,
  created_at: PropTypes.string,
  source_type: PropTypes.string,
  source_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  note: PropTypes.string,
});

RoleBadges.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string),
};

RoleBadges.defaultProps = {
  roles: [],
};

FileRow.propTypes = {
  doc: documentShape.isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  teamOwnerUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onManageAccess: PropTypes.func,
};

FileRow.defaultProps = {
  currentUserId: null,
  teamOwnerUserId: null,
  onManageAccess: undefined,
};

SnapshotRow.propTypes = {
  dealTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupKey: PropTypes.string.isRequired,
  versions: PropTypes.arrayOf(documentShape).isRequired,
  latest: documentShape.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  teamOwnerUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onManageAccess: PropTypes.func,
};

SnapshotRow.defaultProps = {
  dealTeamId: null,
  currentUserId: null,
  teamOwnerUserId: null,
  onManageAccess: undefined,
};

DocumentsTab.propTypes = {
  dealTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documents: PropTypes.arrayOf(documentShape),
  onUpload: PropTypes.func,
  onShareSnapshot: PropTypes.func,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  teamOwnerUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAccessChanged: PropTypes.func,
};

DocumentsTab.defaultProps = {
  dealTeamId: null,
  documents: [],
  onUpload: undefined,
  onShareSnapshot: undefined,
  currentUserId: null,
  teamOwnerUserId: null,
  onAccessChanged: undefined,
};

export default DocumentsTab;
